import { Fragment, useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  BsArrowRepeat,
  BsCalendarEvent,
  BsCameraVideo,
  BsClipboard,
  BsClipboardCheck,
  BsClockHistory,
  BsEnvelope,
  BsGear,
  BsHourglassSplit,
  BsPauseFill,
  BsPeople,
  BsPlayFill,
  BsPlus,
  BsSearch,
  BsX,
} from "react-icons/bs";
import { useGetMeetingSchedulers } from "../../api/queries/useGetMeetingSchedulers";
import {
  usePauseMeetingScheduler,
  usePlayMeetingScheduler,
  useUpdateMeetingScheduler,
} from "../../api/mutations/useMeetingScheduler";
import { useFindUsers } from "../../api/queries/useFindUser";
import { MeetingScheduler, User } from "../../types/Types";
import { isYouthOrEarlyStageMembership } from "../../utils/membershipTier";
import AdminPanelHeader from "./AdminPanelHeader";

const RECURRENCE_OPTIONS = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "BiWeekly", label: "Bi-Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Bimonthly", label: "Bi-Monthly" },
];

const DEFAULT_ORGANIZER_EMAIL = "tech@smbf.global";
/** Matches API GoogleMeetOptions.MeetingDurationMinutes default. */
const MEETING_DURATION_MINUTES = 60;

function organizerEmail(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed || DEFAULT_ORGANIZER_EMAIL;
}

function toLocalInputValue(value?: string | null): string {
  if (!value) return "";
  // Prefer the wall-clock digits as stored (system local), avoid UTC shift.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]}T${match[2]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ukWallClockToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number {
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let i = 0; i < 2; i += 1) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(utcMs));
    const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
    const asUtc = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second"));
    utcMs -= asUtc - utcMs;
  }
  return utcMs;
}

function parseWallClock(value?: string | null): Date | null {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    return new Date(
      ukWallClockToUtcMs(
        Number(match[1]),
        Number(match[2]),
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6] || "0"),
      ),
    );
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ukTimeZoneLabel(): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    timeZoneName: "short",
  }).formatToParts(new Date());
  const shortName = parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
  return `UK (${shortName})`;
}

function formatUkDateTime(d: Date, timeZone: string): string {
  return d.toLocaleString("en-GB", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDisplay(value?: string | null): string {
  if (!value) return "Not set";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) {
    // Stored digits are already UK wall-clock — do not convert through the browser timezone.
    const d = new Date(`${match[1]}T${match[2]}:00Z`);
    if (!Number.isNaN(d.getTime())) {
      return formatUkDateTime(d, "UTC");
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Not set";
  return formatUkDateTime(d, "Europe/London");
}

function getCountdownParts(ms: number): { days: number; hours: string; mins: string; secs: string } {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return { days, hours: pad(hours), mins: pad(mins), secs: pad(secs) };
}

function addRecurrence(date: Date, frequency: string, direction: 1 | -1): Date {
  const d = new Date(date.getTime());
  const f = (frequency || "Weekly").toLowerCase();
  switch (f) {
    case "daily":
      d.setDate(d.getDate() + 1 * direction);
      break;
    case "biweekly":
      d.setDate(d.getDate() + 14 * direction);
      break;
    case "monthly":
      d.setMonth(d.getMonth() + 1 * direction);
      break;
    case "bimonthly":
      d.setMonth(d.getMonth() + 2 * direction);
      break;
    case "weekly":
    default:
      d.setDate(d.getDate() + 7 * direction);
      break;
  }
  return d;
}

/**
 * UI-only: link stays active until the meeting window ends (start + 60 min).
 * After a scheduler run, firstMeetingDateTime is already the next occurrence, so when
 * that start is more than one meeting-length away we treat the URL as the previous cycle.
 */
function isMeetingLinkActive(scheduler: MeetingScheduler, nowMs: number): boolean {
  const url = scheduler.googleMeetUrl?.trim();
  if (!url) return false;

  const start = parseWallClock(scheduler.firstMeetingDateTime);
  if (!start) return true;

  const durationMs = MEETING_DURATION_MINUTES * 60_000;
  const msUntilStart = start.getTime() - nowMs;

  // Next occurrence already rolled forward — current URL belongs to the previous meeting.
  if (msUntilStart > durationMs) {
    const prevStart = addRecurrence(start, scheduler.recurrenceFrequency, -1);
    return nowMs < prevStart.getTime() + durationMs;
  }

  return nowMs < start.getTime() + durationMs;
}

/** Send exact UK wall-clock digits (no browser-timezone conversion). */
function toApiDateTime(localValue: string): string | null {
  if (!localValue) return null;
  return localValue.length === 16 ? `${localValue}:00` : localValue;
}

function recurrenceLabel(value?: string | null): string {
  return (
    RECURRENCE_OPTIONS.find((o) => o.value === value)?.label ||
    value ||
    "Not set"
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(raw: string): string[] {
  const parts = raw.split(/[,;\n\r\t ]+/).map((p) => p.trim()).filter(Boolean);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of parts) {
    const key = part.toLowerCase();
    if (!EMAIL_RE.test(part) || seen.has(key)) continue;
    seen.add(key);
    result.push(part);
  }
  return result;
}

function belongsToSchedulerCategory(
  membershipName?: string | null,
  schedulerCategory?: string | null,
): boolean {
  const member = (membershipName ?? "").trim();
  const category = (schedulerCategory ?? "").trim();
  if (!member || !category) return false;
  if (isYouthOrEarlyStageMembership(member) && isYouthOrEarlyStageMembership(category)) {
    return true;
  }
  return member.toLowerCase() === category.toLowerCase();
}

function joinExternalEmails(emails: string[]): string | null {
  return emails.length ? emails.join(", ") : null;
}

function ExternalAttendeesField({
  schedulerCategory,
  emails,
  onChange,
}: {
  schedulerCategory?: string;
  emails: string[];
  onChange: (emails: string[]) => void;
}) {
  const [input, setInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(input.trim()), 300);
    return () => window.clearTimeout(id);
  }, [input]);

  const searchEnabled = debouncedSearch.length >= 2 && !/[,;]/.test(debouncedSearch);
  const { data: userResponse, isFetching } = useFindUsers({
    enabled: searchEnabled,
    pageSize: 8,
    page: 1,
    searchTerm: searchEnabled ? debouncedSearch : undefined,
    userType: "members",
  });

  const suggestions = useMemo(() => {
    const users = userResponse?.users?.data ?? [];
    const existing = new Set(emails.map((e) => e.toLowerCase()));
    return users.filter((user: User) => {
      const email = user.email?.trim();
      if (!email || existing.has(email.toLowerCase())) return false;
      return !belongsToSchedulerCategory(user.membershipName, schedulerCategory);
    });
  }, [emails, schedulerCategory, userResponse]);

  const addEmails = (incoming: string[]) => {
    if (!incoming.length) return;
    const existing = new Set(emails.map((e) => e.toLowerCase()));
    const next = [...emails];
    for (const email of incoming) {
      const key = email.toLowerCase();
      if (existing.has(key)) continue;
      existing.add(key);
      next.push(email);
    }
    if (next.length !== emails.length) onChange(next);
  };

  const commitInput = () => {
    const parsed = parseEmails(input);
    if (!parsed.length) return false;
    addEmails(parsed);
    setInput("");
    setShowSuggestions(false);
    return true;
  };

  const handleInputChange = (value: string) => {
    if (/[,;\n]/.test(value)) {
      const parts = value.split(/[,;\n]+/);
      const last = parts.pop() ?? "";
      const complete = parseEmails(parts.join(","));
      if (complete.length) addEmails(complete);
      setInput(last.replace(/^\s+/, ""));
      return;
    }
    setInput(value);
    setShowSuggestions(true);
  };

  const handleSelectMember = (user: User) => {
    const email = user.email?.trim();
    if (!email) return;
    addEmails([email]);
    setInput("");
    setShowSuggestions(false);
  };

  return (
    <Form.Group className="mb-0">
      <div className="position-relative">
        <InputGroup>
          <InputGroup.Text className="bg-white">
            <BsSearch className="text-muted" size={14} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => window.setTimeout(() => setShowSuggestions(false), 180)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitInput();
              }
            }}
            placeholder="Search other membership members, or paste emails"
            autoComplete="off"
          />
          <Button
            variant="outline-primary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commitInput}
            disabled={!parseEmails(input).length}
          >
            <BsPlus size={18} />
            Add
          </Button>
        </InputGroup>

        {showSuggestions && searchEnabled && (
          <ListGroup
            className="position-absolute w-100 shadow-sm mt-1"
            style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
          >
            {isFetching && (
              <ListGroup.Item className="small text-muted">Searching members…</ListGroup.Item>
            )}
            {!isFetching && suggestions.length === 0 && (
              <ListGroup.Item className="small text-muted">
                No other-membership members found. Add a custom email and press Enter.
              </ListGroup.Item>
            )}
            {suggestions.map((user) => (
              <ListGroup.Item
                key={user.id}
                action
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectMember(user)}
                className="py-2"
              >
                <div className="fw-semibold">
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
                </div>
                <div className="small text-muted">
                  {user.email}
                  {user.membershipName ? ` · ${user.membershipName}` : ""}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      {emails.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {emails.map((email) => (
            <Badge
              key={email}
              bg="light"
              text="dark"
              className="d-inline-flex align-items-center gap-1 fw-normal border py-2 px-2"
            >
              {email}
              <button
                type="button"
                className="btn btn-link p-0 lh-1 text-secondary"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(emails.filter((item) => item !== email))}
              >
                <BsX size={16} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </Form.Group>
  );
}

const ManageMeetings = () => {
  const { data: schedulers = [], isLoading, isError, error } = useGetMeetingSchedulers();
  const updateMutation = useUpdateMeetingScheduler();
  const playMutation = usePlayMeetingScheduler();
  const pauseMutation = usePauseMeetingScheduler();

  const [selected, setSelected] = useState<MeetingScheduler | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [firstMeeting, setFirstMeeting] = useState("");
  const [nextMeeting, setNextMeeting] = useState("");
  const [recurrence, setRecurrence] = useState("Weekly");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [externalEmails, setExternalEmails] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selected) return;
    setFirstMeeting(toLocalInputValue(selected.firstMeetingDateTime));
    setNextMeeting(toLocalInputValue(selected.nextSchedulerDateTime));
    setRecurrence(selected.recurrenceFrequency || "Weekly");
    setEmailSubject(selected.emailSubject || "");
    setEmailBodyHtml(selected.emailBodyHtml || "");
    setExternalEmails(parseEmails(selected.externalAttendeeEmails || ""));
    setActionError(null);
  }, [selected]);

  const openConfig = (scheduler: MeetingScheduler) => {
    setSelected(scheduler);
    setShowConfig(true);
  };

  const closeConfig = () => {
    setShowConfig(false);
    setSelected(null);
    setActionError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    setActionError(null);

    try {
      await updateMutation.mutateAsync({
        id: selected.id,
        payload: {
          googleAccountEmail: organizerEmail(selected.googleAccountEmail),
          firstMeetingDateTime: toApiDateTime(firstMeeting),
          nextSchedulerDateTime: toApiDateTime(nextMeeting),
          recurrenceFrequency: recurrence,
          emailSubject: emailSubject.trim() || null,
          emailBodyHtml: emailBodyHtml.trim() || null,
          externalAttendeeEmails: joinExternalEmails(externalEmails),
        },
      });
      closeConfig();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to save configuration.";
      setActionError(typeof msg === "string" ? msg : "Failed to save configuration.");
    }
  };

  const handleTogglePlayPause = async (scheduler: MeetingScheduler) => {
    const isRunning = scheduler.status === "Running";
    setBusyId(scheduler.id);
    setActionError(null);

    if (!isRunning) {
      if (!scheduler.firstMeetingDateTime || !scheduler.nextSchedulerDateTime) {
        setActionError("Configure Meeting Date / Time and Scheduler Run Date / Time before starting.");
        setBusyId(null);
        return;
      }
      if (!scheduler.emailSubject?.trim() || !scheduler.emailBodyHtml?.trim()) {
        setActionError("Configure Email Subject and Email Body before starting.");
        setBusyId(null);
        return;
      }
    }

    try {
      if (isRunning) {
        await pauseMutation.mutateAsync(scheduler.id);
      } else {
        if (!scheduler.googleAccountEmail?.trim()) {
          await updateMutation.mutateAsync({
            id: scheduler.id,
            payload: {
              googleAccountEmail: DEFAULT_ORGANIZER_EMAIL,
              firstMeetingDateTime: scheduler.firstMeetingDateTime ?? null,
              nextSchedulerDateTime: scheduler.nextSchedulerDateTime ?? null,
              recurrenceFrequency: scheduler.recurrenceFrequency || "Weekly",
              emailSubject: scheduler.emailSubject ?? null,
              emailBodyHtml: scheduler.emailBodyHtml ?? null,
              externalAttendeeEmails: scheduler.externalAttendeeEmails ?? null,
            },
          });
        }
        await playMutation.mutateAsync(scheduler.id);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        (isRunning ? "Failed to pause scheduler." : "Failed to start scheduler.");
      setActionError(
        typeof msg === "string"
          ? msg
          : isRunning
            ? "Failed to pause scheduler."
            : "Failed to start scheduler."
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyMeetLink = async (scheduler: MeetingScheduler) => {
    const url = scheduler.googleMeetUrl?.trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(scheduler.id);
      window.setTimeout(() => {
        setCopiedId((current) => (current === scheduler.id ? null : current));
      }, 2000);
    } catch {
      setActionError("Failed to copy meeting link.");
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to load meeting schedulers.
        {error instanceof Error ? ` ${error.message}` : ""}
      </div>
    );
  }

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<BsCalendarEvent size={22} />}
        title="Meeting Schedulers"
        description="Automatic Google Meet creation for each membership category"
      />

      {actionError && !showConfig && (
        <div className="alert alert-danger py-2">{actionError}</div>
      )}

      <Row className="g-4">
        {schedulers.map((scheduler) => {
          const isRunning = scheduler.status === "Running";
          const isBusy = busyId === scheduler.id;
          return (
            <Col key={scheduler.id} md={4}>
              <Card className="h-100 shadow border-0 overflow-hidden">
                <Card.Header className={`${isRunning ? "bg-project-gradient" : "bg-secondary"} text-white border-0 py-3`}>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="bg-white bg-opacity-25 rounded-circle p-2 d-inline-flex">
                        <BsPeople size={18} />
                      </span>
                      <div>
                        <div className="fw-bold">{scheduler.membershipCategory}</div>
                        <div className="small text-white-50 d-flex align-items-center gap-1">
                          <BsArrowRepeat size={12} />
                          {recurrenceLabel(scheduler.recurrenceFrequency)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`badge rounded-pill ${
                        isRunning ? "text-bg-success" : "text-bg-light text-dark"
                      }`}
                    >
                      {scheduler.status}
                    </span>
                  </div>
                </Card.Header>

                <Card.Body
                  className="bg-white"
                  style={!isRunning ? { filter: "grayscale(0.4)", opacity: 0.75 } : undefined}
                >
                  <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded-3 bg-info-subtle border border-info-subtle">
                    <span className="rounded-circle bg-info text-white p-2 d-inline-flex flex-shrink-0">
                      <BsCalendarEvent size={16} />
                    </span>
                    <div className="min-w-0">
                      <div className="small text-info fw-semibold">Meeting date / time</div>
                      <div className="fw-semibold text-dark">
                        {formatDisplay(scheduler.firstMeetingDateTime)}{" "}
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded-3 bg-light border">
                    <span className="rounded-circle bg-secondary text-white p-2 d-inline-flex flex-shrink-0">
                      <BsClockHistory size={16} />
                    </span>
                    <div className="min-w-0">
                      <div className="small fw-semibold text-secondary">
                        Scheduler run date / time
                      </div>
                      <div className="fw-semibold text-dark">
                        {formatDisplay(scheduler.nextSchedulerDateTime)}{" "}
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded-3 bg-success-subtle border border-success-subtle">
                    <span className="rounded-circle bg-success text-white p-2 d-inline-flex flex-shrink-0">
                      <BsEnvelope size={16} />
                    </span>
                    <div className="min-w-0">
                      <div className="small text-success fw-semibold">Organizer</div>
                      <div className="fw-semibold text-dark text-break">
                        {organizerEmail(scheduler.googleAccountEmail)}
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const linkActive = isMeetingLinkActive(scheduler, nowMs);
                    const url = scheduler.googleMeetUrl?.trim() || "";
                    const nextRun = parseWallClock(scheduler.nextSchedulerDateTime);
                    const remainingMs = nextRun ? nextRun.getTime() - nowMs : 0;
                    const waitingForNext = !!nextRun && remainingMs > 0;

                    if (!isRunning) {
                      return (
                        <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light border">
                          <span className="rounded-circle bg-secondary text-white p-2 d-inline-flex flex-shrink-0">
                            <BsPauseFill size={16} />
                          </span>
                          <div className="min-w-0 flex-grow-1">
                            <div className="small text-secondary fw-semibold">Meeting link</div>
                            <div className="fw-semibold text-muted">Paused</div>
                          </div>
                        </div>
                      );
                    }

                    if (linkActive) {
                      return (
                        <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-primary-subtle border border-primary-subtle">
                          <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex flex-shrink-0">
                            <BsCameraVideo size={16} />
                          </span>
                          <div className="min-w-0 flex-grow-1">
                            <div className="small text-primary fw-semibold">Meeting link</div>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="fw-semibold text-break small"
                            >
                              {url}
                            </a>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            title={copiedId === scheduler.id ? "Copied" : "Copy link"}
                            aria-label="Copy meeting link"
                            className="flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleCopyMeetLink(scheduler)}
                          >
                            {copiedId === scheduler.id ? (
                              <BsClipboardCheck size={15} />
                            ) : (
                              <BsClipboard size={15} />
                            )}
                          </Button>
                        </div>
                      );
                    }

                    return (
                      <div
                        className="position-relative overflow-hidden rounded-3 border"
                        style={{
                          minHeight: 76,
                          borderColor: "rgba(35, 102, 205, 0.22)",
                          background:
                            "linear-gradient(135deg, rgba(35,102,205,0.06) 0%, rgba(58,151,164,0.08) 100%)",
                        }}
                      >
                        <div
                          className="d-flex align-items-center gap-3 p-2"
                          style={{
                            filter: "blur(4px)",
                            opacity: 0.35,
                            pointerEvents: "none",
                            userSelect: "none",
                          }}
                          aria-hidden="true"
                        >
                          <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex flex-shrink-0">
                            <BsCameraVideo size={16} />
                          </span>
                          <div className="min-w-0 flex-grow-1">
                            <div className="small text-primary fw-semibold">Meeting link</div>
                            <div className="fw-semibold text-break small text-muted">
                              {url || "https://meet.google.com/abc-defg-hij"}
                            </div>
                          </div>
                        </div>

                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center px-3 py-2"
                          style={{
                            backdropFilter: "blur(7px)",
                            WebkitBackdropFilter: "blur(7px)",
                            background: "rgba(255,255,255,0.55)",
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 w-100">
                            <span
                              className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white"
                              style={{
                                width: 36,
                                height: 36,
                                background: "linear-gradient(221deg, #2366cd 0%, #3a97a4 100%)",
                                boxShadow: "0 2px 8px rgba(35,102,205,0.25)",
                              }}
                            >
                              <BsHourglassSplit size={15} />
                            </span>
                            <div className="min-w-0 flex-grow-1">
                              <div
                                className="small fw-semibold mb-0"
                                style={{ color: "#2366cd", letterSpacing: "0.01em" }}
                              >
                                {url ? "Link expired" : "No meeting link"}
                              </div>
                              <div className="small text-muted" style={{ lineHeight: 1.2 }}>
                                {waitingForNext
                                  ? "Next scheduler in"
                                  : "Waiting for next schedule"}
                              </div>
                            </div>
                            {waitingForNext && (() => {
                              const { days, hours, mins, secs } = getCountdownParts(remainingMs);
                              const boxStyle: CSSProperties = {
                                background: "rgba(35,102,205,0.1)",
                                border: "1px solid rgba(35,102,205,0.18)",
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                letterSpacing: "0.03em",
                                fontSize: "0.85rem",
                                color: "#1f4f9c",
                                lineHeight: 1.2,
                                minWidth: 28,
                              };
                              const sepStyle: CSSProperties = {
                                color: "#1f4f9c",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                lineHeight: 1,
                                opacity: 0.7,
                              };
                              const segments = days > 0
                                ? [`${days}d`, hours, mins, secs]
                                : [hours, mins, secs];
                              return (
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                  {segments.map((value, i) => (
                                    <Fragment key={i}>
                                      {i > 0 && <span style={sepStyle}>:</span>}
                                      <div
                                        className="px-1 py-1 rounded-2 text-center fw-bold"
                                        style={boxStyle}
                                      >
                                        {value}
                                      </div>
                                    </Fragment>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </Card.Body>

                <Card.Footer className="bg-light border-0 d-flex gap-2 py-3">
                  <Button
                    size="sm"
                    variant="primary"
                    title="Configure"
                    aria-label="Configure"
                    className="flex-fill d-inline-flex align-items-center justify-content-center gap-2 bg-project-gradient border-0"
                    onClick={() => openConfig(scheduler)}
                  >
                    <BsGear size={15} />
                    Configure
                  </Button>
                  <Button
                    size="sm"
                    variant={isRunning ? "secondary" : "success"}
                    title={isRunning ? "Pause" : "Play"}
                    aria-label={isRunning ? "Pause" : "Play"}
                    disabled={isBusy}
                    className="flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={() => handleTogglePlayPause(scheduler)}
                  >
                    {isBusy ? (
                      <Spinner animation="border" size="sm" />
                    ) : isRunning ? (
                      <>
                        <BsPauseFill size={15} />
                        Pause
                      </>
                    ) : (
                      <>
                        <BsPlayFill size={15} />
                        Play
                      </>
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal show={showConfig} onHide={closeConfig} centered size="lg" backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-project-gradient text-white border-0">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2 text-white">
            <BsGear size={20} />
            {selected?.membershipCategory || "Scheduler"} — Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          {actionError && <div className="alert alert-danger py-2 mb-3">{actionError}</div>}

          <div className="d-flex justify-content-end mb-3">
            <span
              className={`badge rounded-pill ${
                selected?.status === "Running" ? "text-bg-success" : "text-bg-secondary"
              }`}
            >
              {selected?.status || "Paused"}
            </span>
          </div>

          <Form>
            <Card className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex">
                    <BsCalendarEvent size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Schedule settings</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                    <BsEnvelope className="text-primary" />
                    Organizer email
                  </Form.Label>
                  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-2 bg-light border">
                    <BsEnvelope className="text-muted flex-shrink-0" size={14} />
                    <span className="text-break">
                      {organizerEmail(selected?.googleAccountEmail)}
                    </span>
                  </div>
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                        <BsCalendarEvent className="text-info" />
                        Meeting date / time
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={firstMeeting}
                        onChange={(e) => {
                          setFirstMeeting(e.target.value);
                          if (!nextMeeting) setNextMeeting(e.target.value);
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                        <BsClockHistory className="text-secondary" />
                        Scheduler run date / time
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={nextMeeting}
                        onChange={(e) => setNextMeeting(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                    <BsArrowRepeat className="text-success" />
                    Recurrence frequency
                  </Form.Label>
                  <Form.Select
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value)}
                  >
                    {RECURRENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex">
                    <BsPeople size={14} />
                  </span>
                  <div className="fw-semibold text-dark">External attendees</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <ExternalAttendeesField
                  schedulerCategory={selected?.membershipCategory}
                  emails={externalEmails}
                  onChange={setExternalEmails}
                />
              </Card.Body>
            </Card>

            <Card className="mb-0 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-success text-white p-2 d-inline-flex">
                    <BsEnvelope size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Invitation email</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Email subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="{Membership Category} Meeting Invitation"
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Email body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={emailBodyHtml}
                    onChange={(e) => setEmailBodyHtml(e.target.value)}
                    placeholder={
                      "Hi {First Name},\n\n" +
                      "You are invited to the {Membership Category} meeting on {Meeting Date Time}.\n\n" +
                      "Join here: {Google Meet Url}"
                    }
                  />
                  <Form.Text className="text-muted">
                    Required before Play. Tokens: {"{First Name}"}, {"{Membership Category}"},{" "}
                    {"{Meeting Date Time}"}, {"{Google Meet Url}"}.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
          <Button variant="outline-secondary" onClick={closeConfig} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="d-inline-flex align-items-center gap-2 px-4 bg-project-gradient border-0"
          >
            {updateMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageMeetings;
